import { useTranslation } from "react-i18next"
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from "@f2e/ui/components/base"
import { resolveLocalizedText } from "../registry"
import type {
  AgentTemplateTextToSpeechFieldDefinition,
  AgentTemplateToolValue,
} from "../types"
import { ConfigurableFieldShell } from "./ConfigurableFieldShell"
import type { TemplateItemRendererProps } from "./TemplateItemRenderer"

export const TextToSpeechItemRenderer = ({
  item,
  locale,
  value,
  error,
  disabled,
  onChange,
}: TemplateItemRendererProps) => {
  const { t } = useTranslation()
  const definition = item as AgentTemplateTextToSpeechFieldDefinition
  const current = (value as AgentTemplateToolValue | undefined) ?? {
    enabled: Boolean(definition.required || definition.enabledByDefault),
    configuration: {
      language: definition.defaultLanguage,
      slowEnabled: definition.slowEnabledByDefault,
    },
  }
  const configuration = current.configuration
  const language = String(configuration.language || definition.defaultLanguage)
  const slowEnabled =
    configuration.slowEnabled === undefined
      ? definition.slowEnabledByDefault
      : configuration.slowEnabled === true
  const update = (patch: Record<string, unknown>) =>
    onChange({
      enabled: current.enabled,
      configuration: { ...configuration, ...patch },
    })

  return (
    <ConfigurableFieldShell
      id={`template-${definition.key}`}
      title={resolveLocalizedText(definition.label, locale)}
      description={
        definition.description
          ? resolveLocalizedText(definition.description, locale)
          : undefined
      }
      enabled={current.enabled}
      required={definition.required}
      disabled={disabled}
      error={error}
      onEnabledChange={(enabled) => onChange({ enabled, configuration })}
    >
      <div className="space-y-4 border-t border-border pt-4">
        <div className="space-y-2">
          <Label>{t("workspace.agent.templates.textToSpeech.language")}</Label>
          <Select
            value={language}
            disabled={disabled}
            onValueChange={(next) => update({ language: next })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {definition.languageProfiles.map((profile) => (
                <SelectItem key={profile.value} value={profile.value}>
                  {resolveLocalizedText(profile.label, locale)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between gap-4">
          <Label>{t("workspace.agent.templates.textToSpeech.slowVoice")}</Label>
          <Switch
            checked={slowEnabled}
            disabled={disabled}
            onCheckedChange={(checked) => update({ slowEnabled: checked })}
          />
        </div>
      </div>
    </ConfigurableFieldShell>
  )
}
